import {
    flexRender,
    getCoreRowModel,
    getSortedRowModel,
    createColumnHelper,
    useReactTable,
    SortingState,
} from "@tanstack/react-table";
import { useState, useMemo, memo } from "react";

interface SortableTableProps {
    headers: string[];
    rows: string[][];
    tableIndex: number;
}

const SortableTable = memo(({ headers, rows, tableIndex }: SortableTableProps) => {
    const [sorting, setSorting] = useState<SortingState>([]);

    const data = useMemo(() => {
        return rows.map((row, index) => {
            const obj: Record<string, string> = { id: `${tableIndex}-${index}` };
            headers.forEach((header, i) => {
                obj[header] = row[i] || "";
            });
            return obj;
        });
    }, [rows, headers, tableIndex]);

    const columns = useMemo(() => {
        const columnHelper = createColumnHelper<Record<string, string>>();
        return headers.map((header) =>
            columnHelper.accessor(header, {
                header: () => header,
                cell: (info) => info.getValue(),
                sortingFn: (rowA, rowB, columnId) => {
                    const aValue = rowA.getValue(columnId) as string;
                    const bValue = rowB.getValue(columnId) as string;

                    const aNum = parseFloat(aValue);
                    const bNum = parseFloat(bValue);

                    if (!isNaN(aNum) && !isNaN(bNum)) {
                        return aNum - bNum;
                    }

                    return aValue.localeCompare(bValue);
                },
            })
        );
    }, [headers]);

    const table = useReactTable({
        data,
        columns,
        state: {
            sorting,
        },
        onSortingChange: setSorting,
        getCoreRowModel: getCoreRowModel(),
        getSortedRowModel: getSortedRowModel(),
    });

    return (
        <div className="my-4 overflow-x-auto">
            <table className="w-full border-collapse overflow-hidden rounded-lg border border-border">
                <thead>
                    {table.getHeaderGroups().map((headerGroup) => (
                        <tr key={headerGroup.id}>
                            {headerGroup.headers.map((header) => (
                                <th
                                    key={header.id}
                                    className="cursor-pointer select-none border border-border bg-muted/50 px-3 py-2 text-left font-semibold hover:bg-muted"
                                    onClick={header.column.getToggleSortingHandler()}
                                >
                                    <div className="flex items-center justify-between">
                                        {flexRender(header.column.columnDef.header, header.getContext())}
                                        <span className="ml-2 text-sm">
                                            {{
                                                asc: "↑",
                                                desc: "↓",
                                            }[header.column.getIsSorted() as string] ?? "↕"}
                                        </span>
                                    </div>
                                </th>
                            ))}
                        </tr>
                    ))}
                </thead>
                <tbody>
                    {table.getRowModel().rows.map((row) => (
                        <tr key={row.id} className="transition-colors hover:bg-muted/30">
                            {row.getVisibleCells().map((cell) => (
                                <td key={cell.id} className="border border-border px-3 py-2">
                                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
});

export default SortableTable;